import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { logActivity } from '@/lib/activityLog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Upload, Package } from 'lucide-react'
import { CanvaDesignButton } from '@/components/Design/CanvaDesignButton'

const LANG_LABEL: Record<string, string> = { en: 'English', fr: 'French', pt: 'Portuguese' }
const LANG_FLAG: Record<string, string>  = { en: '🇬🇧', fr: '🇫🇷', pt: '🇵🇹' }

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function MediaKitsView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadLang, setUploadLang] = useState('')
  const [uploadVersion, setUploadVersion] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const { data: orgLanguages } = useQuery({
    queryKey: ['org-languages', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('org_languages')
        .select('*')
        .eq('org_id', orgId!)
        .order('language_code')
      return (data || []) as any[]
    }
  })

  const { data: mediaKits } = useQuery({
    queryKey: ['org-media-kits', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('org_media_kits')
        .select('id, language_code, file_url, version, published_at, uploaded_by')
        .eq('org_id', orgId!)
        .order('published_at', { ascending: false })
      return (data || []) as any[]
    }
  })

  if (!can('media_kits', 'view')) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        You do not have access to the Media Kits module.
      </div>
    )
  }

  const canUpload = can('media_kits', 'edit')

  const openUploadFor = (langCode: string) => {
    setUploadLang(langCode)
    setUploadVersion('')
    setUploadFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUploadOpen(true)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast({ title: 'Only PDF files are accepted.', variant: 'destructive' })
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: 'File must be 15 MB or smaller.', variant: 'destructive' })
      return
    }
    setUploadFile(file)
  }

  const handleUpload = async () => {
    if (!uploadFile || !uploadLang || !uploadVersion.trim() || !orgId) return
    setUploading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const path = `${orgId}/${uploadLang}/${uploadVersion.trim()}-media-kit.pdf`

      const { error: uploadError } = await supabase.storage
        .from('media-kits')
        .upload(path, uploadFile, { upsert: true, contentType: 'application/pdf' })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('media-kits')
        .getPublicUrl(path)

      const fileUrl = urlData.publicUrl

      const { error: insertError } = await supabase
        .from('org_media_kits')
        .insert({
          org_id:       orgId,
          language_code: uploadLang,
          file_url:     fileUrl,
          version:      uploadVersion.trim(),
          published_at: new Date().toISOString(),
          uploaded_by:  user?.id ?? null,
        })
      if (insertError) throw insertError

      const { error: langError } = await supabase
        .from('org_languages')
        .update({ is_active: true })
        .eq('org_id', orgId)
        .eq('language_code', uploadLang)
      if (langError) throw langError

      await logActivity({
        orgId,
        actionType: 'media_kit_uploaded',
        description: `Media kit published for ${LANG_LABEL[uploadLang] ?? uploadLang} (${uploadVersion.trim()})`,
        metadata: { language_code: uploadLang, version: uploadVersion.trim() },
      })

      toast({ title: `Media kit published for ${LANG_LABEL[uploadLang] ?? uploadLang}.` })
      setUploadOpen(false)
      qc.invalidateQueries({ queryKey: ['org-media-kits', orgId] })
      qc.invalidateQueries({ queryKey: ['org-languages', orgId] })
    } catch (e: any) {
      toast({ title: e.message || 'Upload failed.', variant: 'destructive' })
    } finally {
      setUploading(false)
    }
  }

  const langs = (orgLanguages || [])
  const hasLangs = langs.length > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Kits</h1>
          <p className="text-sm text-muted-foreground mt-1 text-justify max-w-xl">
            Official press kits for accredited journalists. Each active language kit is
            accessible to journalists via the CONORBIT Journalist Portal.
          </p>
        </div>
        {canUpload && hasLangs && (
          <Button onClick={() => openUploadFor(langs[0].language_code)} className="shrink-0">
            <Upload className="w-4 h-4 mr-2" />
            Upload New Kit
          </Button>
        )}
      </div>

      {!hasLangs ? (
        <div className="border border-dashed rounded-xl p-10 text-center text-muted-foreground text-sm space-y-2">
          <Package className="w-8 h-8 mx-auto opacity-30" />
          <p>No languages configured for this organisation.</p>
          <p className="text-justify text-xs">Configure languages in Org Settings → Languages tab first.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {langs.map((lang: any) => {
            const kit = (mediaKits || []).find((k: any) => k.language_code === lang.language_code)
            const flag = LANG_FLAG[lang.language_code] ?? '🌐'
            const langName = LANG_LABEL[lang.language_code] ?? lang.language_code.toUpperCase()

            return (
              <div key={lang.language_code} className="bg-white border border-[#DDD8CE] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{flag}</span>
                  <span className="font-semibold text-[13px]">{langName}</span>
                  <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    lang.is_active
                      ? 'bg-[#E1F5EE] text-[#0F6E56]'
                      : 'bg-[#FEF3C7] text-[#854F0B]'
                  }`}>
                    {lang.is_active ? 'Active' : 'No kit'}
                  </span>
                </div>

                {kit ? (
                  <div className="space-y-2">
                    <div className="text-[12px] text-muted-foreground">
                      Version {kit.version || '1.0'}
                    </div>
                    {kit.published_at && (
                      <div className="text-[12px] text-muted-foreground">
                        Published {formatDate(kit.published_at)}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button size="sm" variant="outline" asChild className="text-xs h-7">
                        <a href={kit.file_url} target="_blank" rel="noopener noreferrer">
                          Preview
                        </a>
                      </Button>
                      <Button size="sm" variant="outline" asChild className="text-xs h-7">
                        <a href={kit.file_url} download>
                          Download
                        </a>
                      </Button>
                      <CanvaDesignButton defaultType="media_kit" label="Design in Canva" size="sm" />
                      {canUpload && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={() => openUploadFor(lang.language_code)}
                        >
                          Upload New Version
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[12px] text-muted-foreground text-justify leading-relaxed">
                      No media kit has been published for this language. Upload a kit to make it
                      available to accredited journalists.
                    </p>
                    {canUpload && (
                      <Button
                        size="sm"
                        onClick={() => openUploadFor(lang.language_code)}
                        className="text-xs bg-[#C8A84B] hover:bg-[#B8983B] text-white h-7"
                      >
                        <Upload className="w-3 h-3 mr-1.5" />
                        Upload Kit
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Kit version history */}
      {(mediaKits || []).length > 0 && (
        <div>
          <h2 className="text-sm font-semibold mb-3">Version History</h2>
          <div className="bg-white border border-[#DDD8CE] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Language</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground">Version</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground">Published</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground w-20" />
                </tr>
              </thead>
              <tbody>
                {(mediaKits || []).map((kit: any) => (
                  <tr key={kit.id} className="border-b last:border-0 hover:bg-muted/10">
                    <td className="px-4 py-2.5">
                      <span className="mr-1.5">{LANG_FLAG[kit.language_code] ?? '🌐'}</span>
                      {LANG_LABEL[kit.language_code] ?? kit.language_code.toUpperCase()}
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground font-mono text-xs">
                      {kit.version || '1.0'}
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">
                      {formatDate(kit.published_at)}
                    </td>
                    <td className="px-2 py-2.5">
                      <Button size="sm" variant="ghost" asChild className="h-6 text-xs">
                        <a href={kit.file_url} target="_blank" rel="noopener noreferrer">
                          Open
                        </a>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload sheet */}
      <Sheet open={uploadOpen} onOpenChange={setUploadOpen}>
        <SheetContent side="right" className="w-[480px]">
          <SheetHeader>
            <SheetTitle>
              Upload {LANG_LABEL[uploadLang] ?? uploadLang} Media Kit
            </SheetTitle>
            <p className="text-justify text-sm text-muted-foreground">
              PDF only · maximum 15 MB · replaces the current published kit
            </p>
          </SheetHeader>

          <div className="space-y-5 mt-5">
            <div className="space-y-1.5">
              <Label>Version Label</Label>
              <Input
                value={uploadVersion}
                onChange={e => setUploadVersion(e.target.value)}
                placeholder="e.g. 2026-Q2 or v1.1"
              />
              <p className="text-justify text-xs text-muted-foreground">
                Used as the filename: {uploadVersion ? `${uploadVersion}-media-kit.pdf` : 'version-media-kit.pdf'}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>PDF File</Label>
              <div
                className="border-2 border-dashed border-muted rounded-lg p-6 text-center cursor-pointer hover:border-[#C8A84B]/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFile ? (
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-green-600">{uploadFile.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB, click to change
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="w-6 h-6 text-muted-foreground mx-auto" />
                    <div className="text-sm text-muted-foreground">
                      Click to select PDF
                    </div>
                    <div className="text-xs text-muted-foreground">Max 15 MB</div>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <Button
              className="w-full bg-[#C8A84B] hover:bg-[#B8983B] text-white"
              disabled={uploading || !uploadFile || !uploadVersion.trim()}
              onClick={handleUpload}
            >
              {uploading ? 'Uploading…' : 'Publish Media Kit'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
